import { Images } from '@/assets/images';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { type Platform } from '@/lib/platform';
import { Download } from 'lucide-react';
import { type ReactElement } from 'react';
import { InstallTrigger } from './InstallTrigger';

interface DownloadMeta {
  platform: Platform;
  version: string;
  url: string;
}

interface VsixDirectDownloadProps {
  vsixUrl: string;
  vsixVersion: string;
  actualPlatform: Platform | null;
  vsixUrlFallback: string;
  platformDownloadMeta: DownloadMeta[];
  vscodeCopilotUrl: string;
  vscodeCopilotDownloadButtonText: string;
  cursorInstallationInstruction: string;
  vsCodeInstallationInstruction: string;
}

/**
 * VSIX Direct Download section component that displays download options
 * for Kirin Cursor and Kirin VSCode Copilot extensions.
 */
export function VsixDirectDownload({
  vsixUrl,
  vsixVersion,
  actualPlatform,
  vsixUrlFallback,
  platformDownloadMeta,
  vscodeCopilotUrl,
  vscodeCopilotDownloadButtonText,
  cursorInstallationInstruction,
  vsCodeInstallationInstruction,
}: VsixDirectDownloadProps): ReactElement {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Download className="h-5 w-5" />
          Download VSIX Directly
        </CardTitle>
        <CardDescription>
          Download the extension file for manual installation.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Cursor VSIX */}
            <div className="space-y-3">
              <div className="font-medium flex items-center gap-2">
                <Images.CursorIcon className="size-5" />
                Kirin for Cursor
              </div>
              {vsixUrl ? (
                <InstallTrigger
                  environment="Cursor"
                  userPlatform={actualPlatform}
                  fallbackDownloadMeta={{
                    platform: actualPlatform,
                    version: vsixVersion,
                    url: vsixUrlFallback,
                  }}
                  platformDownloadMeta={platformDownloadMeta}
                />
              ) : (
                <Button asChild className="w-full">
                  <a
                    href="https://github.com/knostic/kirin/releases"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View Releases
                  </a>
                </Button>
              )}
              <div className="text-xs text-muted-foreground">
                {cursorInstallationInstruction}
              </div>
            </div>

            {/* VS Code Copilot VSIX */}
            <div className="space-y-3">
              <div className="font-medium flex items-center gap-2">
                <Images.VSCodeIcon className="size-5" />
                Kirin for VSCode Copilot
              </div>
              <Button asChild className="w-full">
                <a
                  href={vscodeCopilotUrl}
                  download
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {vscodeCopilotDownloadButtonText}
                </a>
              </Button>
              <div className="text-xs text-muted-foreground">
                {vsCodeInstallationInstruction}
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

VsixDirectDownload.displayName = 'VsixDirectDownload';
